import type { Dict } from "@/types/dict"
import { getNextFormSchema, schemaToState, type FormSchema, type ProcessedFormSchema } from "./schema"
import { getNextLayoutNode, type FormLayoutDefinition, type FormLayoutNode } from "./layout"
import type { FormInstance } from "./instance"

export type FormUpdate<State extends Dict, ComputedData extends Dict = Dict, ExternalData extends Dict = Dict> = {
  schema: ProcessedFormSchema<State, ComputedData>
  state: State
  layout: FormLayoutNode<State, ComputedData, ExternalData>
}

export type GetNextFormStateProps<State extends Dict, ComputedData extends Dict = Dict, ExternalData extends Dict = Dict> = {
  schema: FormSchema<State, State, void, ComputedData> | ProcessedFormSchema<State, ComputedData>
  layout: FormLayoutDefinition<State, ComputedData, ExternalData>
  cache: Map<string, FormLayoutNode<State, ComputedData, ExternalData>>;
  computedData: ComputedData;
  previousState?: State
}

export const getNextFormState = <State extends Dict, ComputedData extends Dict = Dict, ExternalData extends Dict = Dict>(
  props: GetNextFormStateProps<State, ComputedData, ExternalData>,
  form: FormInstance<State, ComputedData, ExternalData>
): FormUpdate<State, ComputedData, ExternalData> => {
  const prev = props.previousState || schemaToState<State, ComputedData>(props.schema)
  const nextSchema = getNextFormSchema<State, ComputedData>(props.schema, prev, props.computedData)
  const nextState = schemaToState<State, ComputedData>(nextSchema)
  const nextLayout = getNextLayoutNode<State, ComputedData, ExternalData>(props.layout, nextState, props.cache, form);

  return { schema: nextSchema, state: nextState, layout: nextLayout }
}
